import React from 'react';
import { Box, Button, FormControl, MenuItem, Select, TextField, Typography } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '@/__core/constants/pagination';

export interface BulkActionOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface BulkActionsPageSizeSelectProps {
  pageSize: number;
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
}

export function BulkActionsPageSizeSelect({
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
}: BulkActionsPageSizeSelectProps) {
  const handlePageSizeChange = (event: SelectChangeEvent<string>) => {
    const nextPageSize = Number(event.target.value);
    if (!Number.isNaN(nextPageSize) && nextPageSize > 0) {
      onPageSizeChange?.(nextPageSize);
    }
  };

  return (
    <FormControl size='small' sx={{ minWidth: 90 }}>
      <Select
        value={String(pageSize)}
        onChange={handlePageSizeChange}
        sx={{
          height: 34,
          '& .MuiSelect-select': {
            py: 0.5,
            pr: 3,
          }
        }}
      >
        {pageSizeOptions.map((option) => (
          <MenuItem key={option} value={String(option)}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

interface AppBulkActionsProps {
  selectedCount: number;
  itemLabel?: string;
  actions?: BulkActionOption[];
  action?: string;
  loading?: boolean;
  onActionChange?: (action: string) => void;
  onApply?: (action?: string) => void;
  // For dropdown-based actions (like in StoreList)
  useDropdown?: boolean;
  dropdownLabel?: string;
}

export default function AppBulkActions({
  selectedCount,
  itemLabel = 'items',
  actions = [],
  action = '',
  loading = false,
  onActionChange,
  onApply,
  useDropdown = false,
  dropdownLabel = 'Thao tác',
}: AppBulkActionsProps) {
  const [currentAction, setCurrentAction] = React.useState(action);

  React.useEffect(() => {
    setCurrentAction(action);
  }, [action]);

  if (selectedCount === 0) {
    return null;
  }

  if (useDropdown) {
    // Dropdown mode - returns just the dropdown button (for components like StoreList)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant='body2' sx={{ color: '#374151' }}>
          Đã chọn {selectedCount} {itemLabel}
        </Typography>
      </Box>
    );
  }

  // Standard mode - returns full action controls
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant='body2' sx={{ color: '#374151' }}>
        Đã chọn {selectedCount} {itemLabel}
      </Typography>
      {actions.length > 0 ? (
        <>
          <FormControl size='small' sx={{ minWidth: 260 }}>
            <Select
              value={currentAction}
              onChange={(event: SelectChangeEvent<string>) => {
                const nextAction = event.target.value;
                setCurrentAction(nextAction);
                onActionChange?.(nextAction);
              }}
              disabled={loading}
              displayEmpty
            >
              {actions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant='contained'
            size='small'
            type='button'
            onClick={() => {
              console.log('[AppBulkActions] Apply clicked', {
                currentAction,
                selectedCount,
                hasOnApply: Boolean(onApply),
              });
              onApply?.(currentAction);
            }}
            disabled={!currentAction || loading}
            sx={{ minWidth: 84, height: 32, px: 1.5 }}
          >
            {loading ? 'Đang xử lý...' : 'Áp dụng'}
          </Button>
        </>
      ) : (
        <TextField
          select
          size='small'
          value={action}
          onChange={(event) => onActionChange?.(event.target.value)}
          disabled={loading}
          sx={{ minWidth: 260 }}
        >
          <MenuItem value=''>Chọn action cho items đã chọn</MenuItem>
        </TextField>
      )}
    </Box>
  );
}
